import {
  DomainBorrowerStatus,
  type DtoBorrower,
  type DtoBorrowerConsentUpdateReq,
  type DtoBorrowerUpdateReq,
  type DtoCreateApplicationReq,
  type DtoAcceptCoBorrowerInviteReq,
  type DomainBorrowerEmploymentStatus,
  type DomainCitizenshipType,
  type DomainMaritalStatusType,
} from "@porchpass/api-client";
import {
  formatConsentSsnForApi,
  toConsentFormData,
} from "src/forms/adapters/pii";
import {
  commaSeparatedNumbersToArray,
  createIsoDate,
  parseIsoDate,
  safeNumber,
  zeroDate,
  zeroPhoneNumber,
} from "src/utils";
import { requiredField } from "./required";

export type RegisterFormData = {
  dealer_id?: string;
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  suffix?: string;
  email?: string;
  confirm_email?: string;
  phone_number?: string;
  sms_support_opt_out?: boolean;
  hasPreviousData?: boolean;
  status?: string;
  ignore_duplicate?: boolean;
};

export type PersonalInfoFormData = {
  marital_status_type?: string;
  employment_status?: string;
  dependents_ages_years?: string;
  originalDependentsArray?: number[];
  citizenship_type?: string;
  nearest_relative_name?: string;
  nearest_relative_relationship?: string;
  nearest_relative_phone_number?: string;
  bankruptcy_month?: string;
  bankruptcy_year?: string;
};

export type ConsentFormData = {
  dob_day?: string;
  dob_month?: string;
  dob_year?: string;
  ssn_start?: string;
  ssn_middle?: string;
  ssn_end?: string;
  is_acknowledged?: boolean;
  disclaimer?: string;
};

export function toCreateApplicationReq(
  data: RegisterFormData,
): DtoCreateApplicationReq {
  return {
    dealer_id: requiredField(data.dealer_id, "dealer_id"),
    first_name: requiredField(data.first_name, "first_name"),
    last_name: requiredField(data.last_name, "last_name"),
    middle_name: data.middle_name,
    suffix: data.suffix,
    email: requiredField(data.email, "email"),
    phone_number: requiredField(data.phone_number, "phone_number"),
    sms_support_opt_out: requiredField(
      data.sms_support_opt_out,
      "sms_support_opt_out",
    ),
  };
}

export function fromBorrowerRegister(data: DtoBorrower): RegisterFormData {
  return {
    ...data,
    confirm_email: data.email,
    hasPreviousData: Boolean(
      data.first_name && data.last_name && data.email && data.phone_number,
    ),
  };
}

export function toBorrowerUpdateReq(
  data: RegisterFormData,
): DtoBorrowerUpdateReq {
  return {
    first_name: requiredField(data.first_name, "first_name"),
    last_name: requiredField(data.last_name, "last_name"),
    middle_name: data.middle_name,
    suffix: data.suffix,
    email: requiredField(data.email, "email"),
    phone_number: requiredField(data.phone_number, "phone_number"),
    status:
      data.status === DomainBorrowerStatus.BorrowerInvited
        ? DomainBorrowerStatus.BorrowerInProgress
        : undefined,
    ...(data.ignore_duplicate ? { ignore_duplicate: true } : {}),
  };
}

export function toAcceptCoBorrowerInviteReq(
  data: RegisterFormData,
): DtoAcceptCoBorrowerInviteReq {
  return {
    first_name: requiredField(data.first_name, "first_name"),
    last_name: requiredField(data.last_name, "last_name"),
    middle_name: data.middle_name,
    suffix: data.suffix,
    email: requiredField(data.email, "email"),
    phone_number: requiredField(data.phone_number, "phone_number"),
    ...(data.ignore_duplicate ? { ignore_duplicate: true } : {}),
  };
}

export function fromBorrowerPersonalInfo(
  data: DtoBorrower,
): PersonalInfoFormData {
  return {
    marital_status_type: data.marital_status_type ?? "",
    employment_status: data.employment_status ?? "",
    dependents_ages_years: data.dependents_ages_years?.join(","),
    originalDependentsArray: data?.dependents_ages_years,
    citizenship_type: data.citizenship_type ?? "",
    nearest_relative_name: data.nearest_relative_name ?? "",
    nearest_relative_relationship: data.nearest_relative_relationship ?? "",
    nearest_relative_phone_number:
      data.nearest_relative_phone_number === zeroPhoneNumber || null
        ? ""
        : data.nearest_relative_phone_number,
    ...(() => {
      if (!data.bankruptcy_date) {
        return {};
      }
      if (data.bankruptcy_date === zeroDate) {
        return {
          bankruptcy_month: "",
          bankruptcy_year: "",
        };
      }
      const { month, year } = parseIsoDate(data.bankruptcy_date);
      return {
        bankruptcy_month: month,
        bankruptcy_year: year,
      };
    })(),
  };
}

export function toCitizenshipUpdateReq(
  data: PersonalInfoFormData,
): DtoBorrowerUpdateReq {
  return {
    citizenship_type: data.citizenship_type as DomainCitizenshipType,
  };
}

export function toMaritalStatusUpdateReq(
  data: PersonalInfoFormData,
): DtoBorrowerUpdateReq {
  return {
    marital_status_type: data.marital_status_type as DomainMaritalStatusType,
  };
}

export function toDependentsUpdateReq(
  data: PersonalInfoFormData,
): DtoBorrowerUpdateReq {
  return {
    dependents_ages_years: commaSeparatedNumbersToArray(
      data.dependents_ages_years,
    ),
    nearest_relative_name: data.nearest_relative_name,
    nearest_relative_relationship: data.nearest_relative_relationship,
    ...(data.nearest_relative_phone_number
      ? {
          nearest_relative_phone_number: data.nearest_relative_phone_number,
        }
      : {}),
  };
}

export function toBankruptcyUpdateReq(
  data: PersonalInfoFormData,
): DtoBorrowerUpdateReq {
  const year = safeNumber(data.bankruptcy_year);
  const month = safeNumber(data.bankruptcy_month);
  return {
    bankruptcy_date: !year && !month ? zeroDate : createIsoDate(year, month),
  };
}

export function toEmploymentStatusUpdateReq(
  data: PersonalInfoFormData,
): DtoBorrowerUpdateReq {
  return {
    employment_status: data.employment_status as DomainBorrowerEmploymentStatus,
  };
}

export function fromBorrowerConsent(data: DtoBorrower): ConsentFormData {
  return toConsentFormData({
    ssn: data.ssn,
    date_of_birth: data.date_of_birth,
    acknowledged_at: data.acknowledged_at,
    first_name: data.first_name,
    middle_name: data.middle_name,
    last_name: data.last_name,
  });
}

export function toConsentUpdateReq(
  data: ConsentFormData,
): DtoBorrowerConsentUpdateReq {
  return {
    ssn: formatConsentSsnForApi(data),
    date_of_birth: createIsoDate(
      safeNumber(data.dob_year),
      safeNumber(data.dob_month),
      safeNumber(data.dob_day),
    ),
    is_acknowledged: !!data.is_acknowledged,
  };
}
